/**
 * Documents Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useDocuments hook.
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Search,
  Plus,
  FolderPlus,
  Filter,
  Loader2,
  LayoutGrid,
  List,
  X,
  Save,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { documentsAPI } from '../lib/api';
import { getApiErrorMessage } from '../lib/api/types/common';
import {
  useDocuments,
  StatsCards,
  FolderView,
  ListView,
  UploadDialog,
} from './document-manager';
import DocumentDetailModal from './document-manager/DocumentDetailModal';
import { VisibilityField } from './document-manager/VisibilityField';
import { useIsCustomer } from '../hooks/usePortalMode';

// Supplier-subfolder layout. Lifted to the page so the toggle can sit next
// to the search bar while the actual rendering lives in FolderView.
type SubLayout = 'tiles' | 'list';
const SUB_LAYOUT_KEY = 'doc_sub_layout';

function NewFolderDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await documentsAPI.createCategory({ name: name.trim(), description: description.trim() });
      toast.success(`Folder "${name.trim()}" created`);
      setName(''); setDescription('');
      onCreated();
      onClose();
    } catch (err) {
      // CR-5326: surface the backend's friendly duplicate-folder message
      // ("A folder with this name already exists."). Helix's error
      // middleware wraps validation errors as
      // {details: {fields: {name: [msg]}}} — getApiErrorMessage walks
      // that shape so we don't have to duplicate the unwrap logic.
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = status === 403
        ? 'Only customer admins can create folders.'
        : getApiErrorMessage(err, 'Failed to create folder');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Name <span className="text-red-500">*</span></Label>
            <Input id="folder-name" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Compliance Certificates" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="folder-desc">Description</Label>
            <Textarea id="folder-desc" value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Optional — shown under the folder title" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving || !name.trim()}>
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</> : 'Create Folder'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Documents() {
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [subLayout, setSubLayoutState] = useState<SubLayout>(() => {
    if (typeof window === 'undefined') return 'tiles';
    return (window.localStorage.getItem(SUB_LAYOUT_KEY) as SubLayout | null) || 'tiles';
  });
  const setSubLayout = (next: SubLayout) => {
    setSubLayoutState(next);
    if (typeof window !== 'undefined') window.localStorage.setItem(SUB_LAYOUT_KEY, next);
  };
  const {
    // View state
    viewMode,

    // Loading states
    loading,
    error,
    uploading,

    // Search
    searchTerm,
    handleSearchChange,

    // Filters
    showFilters,
    filters,
    hasActiveFilters,
    handleToggleFilters,
    handleFilterChange,
    handleClearFilters,

    // Data
    stats,
    categoriesWithDocs,
    documents,
    categories,
    pagination,

    // Upload dialog
    uploadDialog,
    uploadForm,
    setUploadForm,
    handleUploadDialogOpen,
    handleUploadDialogClose,
    handleFileChange,
    handleDroppedFile,
    handleUploadSubmit,

    // Actions
    fetchData,
    handleDownload,
    handleDelete,
    handleEdit,
    handlePreview,

    // Edit dialog
    editDialog,
    editForm,
    setEditForm,
    handleEditDialogClose,
    handleEditSubmit,
    editSaving,

    // CR-5330: Detail dialog
    detailDialog,
    detailDoc,
    handleOpenDetail,
    handleDetailDialogClose,
  } = useDocuments();

  const isCustomer = useIsCustomer();

  // CR-5462: Document acknowledgement notifications include ?doc=<id> in
  // their action_url so the notification opens the specific document
  // instead of the generic Documents list. The default view is folder-
  // grouped (categoriesWithDocs is populated, flat `documents` is not),
  // so we look in both to handle either view mode the user lands in.
  // handledDocRef guards against re-opening if the same id is processed
  // twice (e.g. fetchData refresh) within the same page lifecycle.
  const [searchParams, setSearchParams] = useSearchParams();
  const handledDocRef = useRef<string | null>(null);
  useEffect(() => {
    const docParam = searchParams.get('doc');
    if (!docParam || loading || handledDocRef.current === docParam) return;
    let target = documents?.find(d => String(d.id) === docParam);
    if (!target && categoriesWithDocs?.length) {
      for (const cat of categoriesWithDocs) {
        const found = cat.documents?.find(d => String(d.id) === docParam);
        if (found) { target = found; break; }
      }
    }
    if (!target) return;
    handledDocRef.current = docParam;
    handleOpenDetail(target);
    // Strip the param so a back/forward or refresh doesn't re-open.
    const next = new URLSearchParams(searchParams);
    next.delete('doc');
    setSearchParams(next, { replace: true });
  }, [searchParams, documents, categoriesWithDocs, loading, handleOpenDetail, setSearchParams]);

  // CR-5324: Page-level drag-and-drop. Without preventDefault on dragover
  // + drop, the browser tries to navigate to the file URL when a file is
  // dropped anywhere on the page — that's the "page refreshes and remains
  // blank" symptom. We attach to window so the drop zone is the whole
  // page, mirror the visual state with a dragCounter (handles nested
  // dragenter/dragleave events without flicker), and only act on customer
  // sessions because suppliers don't have an Upload button on this page.
  const [isDragging, setIsDragging] = useState(false);
  useEffect(() => {
    if (!isCustomer) return;
    let dragCounter = 0;
    const onDragEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      dragCounter += 1;
      if (dragCounter === 1) setIsDragging(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      dragCounter = Math.max(0, dragCounter - 1);
      if (dragCounter === 0) setIsDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounter = 0;
      setIsDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) handleDroppedFile(file);
    };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [isCustomer, handleDroppedFile]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={fetchData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* CR-5324: drag-and-drop overlay. Renders when a file is dragged
          over any part of the page; on drop, the upload dialog opens with
          the file pre-selected. */}
      {isDragging && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-primary/10 border-4 border-dashed border-primary pointer-events-none"
          aria-hidden="true"
        >
          <div className="rounded-lg bg-background border-2 border-primary px-8 py-6 shadow-xl text-center">
            <Upload className="h-10 w-10 mx-auto text-primary mb-2" />
            <p className="text-lg font-semibold">Drop file to upload</p>
            <p className="text-sm text-muted-foreground mt-1">
              We'll open the upload form with this file selected.
            </p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Documents</h1>
          <p className="text-muted-foreground mt-2">
            Manage and organize your documents and files.
          </p>
        </div>
        {isCustomer && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setNewFolderOpen(true)}>
              <FolderPlus className="h-4 w-4 mr-2" />
              New Folder
            </Button>
            <Button onClick={handleUploadDialogOpen}>
              <Plus className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          </div>
        )}
      </div>
      <NewFolderDialog
        open={newFolderOpen}
        onClose={() => setNewFolderOpen(false)}
        onCreated={fetchData}
      />

      {/* Stats Cards */}
      {stats && <StatsCards stats={stats} />}

      {/* Search and View Toggle */}
      <div className="flex gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
        {/* Sub-layout toggle — tile grid vs collapsible row list for the
            supplier sub-folders rendered inside FolderView. Sits next to
            the search bar because it's the more frequently flipped toggle;
            the outer folder-vs-list viewMode now lives in the FolderView
            card header (less frequently changed). */}
        {viewMode === 'folder' && (
          <div className="inline-flex rounded-md border bg-background overflow-hidden">
            <Button
              variant={subLayout === 'tiles' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setSubLayout('tiles')}
              title="Tile layout"
              className="rounded-none"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={subLayout === 'list' ? 'default' : 'ghost'}
              size="icon"
              onClick={() => setSubLayout('list')}
              title="List layout"
              className="rounded-none border-l"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        )}
        <Button
          variant={hasActiveFilters ? 'default' : 'outline'}
          onClick={handleToggleFilters}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <span className="ml-1 bg-white text-primary rounded-full w-5 h-5 text-xs flex items-center justify-center">
              !
            </span>
          )}
        </Button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-muted/50 border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Filter Documents</h3>
            <div className="flex gap-2">
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Document Type Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Document Type</label>
              <Select
                value={filters.documentType}
                onValueChange={(value) => handleFilterChange('documentType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="file">File</SelectItem>
                  <SelectItem value="url">URL / Link</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Folder Filter (CR-5326: was "Category") */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Folder</label>
              <Select
                value={filters.category}
                onValueChange={(value) => handleFilterChange('category', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Folders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Folders</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Visibility Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Visibility</label>
              <Select
                value={filters.visibility}
                onValueChange={(value) => handleFilterChange('visibility', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="shared">Shared</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Folder View */}
      {viewMode === 'folder' && (
        <FolderView
          categories={categoriesWithDocs}
          searchTerm={searchTerm}
          onDownload={handleDownload}
          onUploadOpen={handleUploadDialogOpen}
          onDelete={isCustomer ? handleDelete : undefined}
          onEdit={isCustomer ? handleEdit : undefined}
          onPreview={handlePreview}
          onOpenDetail={handleOpenDetail}
          subLayout={subLayout}
        />
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <ListView
          documents={documents}
          searchTerm={searchTerm}
          onDownload={handleDownload}
          onUploadOpen={handleUploadDialogOpen}
          onDelete={isCustomer ? handleDelete : undefined}
          onEdit={isCustomer ? handleEdit : undefined}
          onPreview={handlePreview}
          onOpenDetail={handleOpenDetail}
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalCount={pagination.totalCount}
          pageSize={pagination.pageSize}
          onPageChange={pagination.goToPage}
          onPageSizeChange={pagination.setPageSize}
        />
      )}

      {/* CR-5330: Document details popup. CR-5328: refresh after a
          successful version upload so the new version label/size lands
          on the tile/row immediately. */}
      <DocumentDetailModal
        document={detailDoc}
        open={detailDialog.isOpen}
        onClose={handleDetailDialogClose}
        onDownload={handleDownload}
        onPreview={handlePreview}
        onEdit={isCustomer ? handleEdit : undefined}
        onDelete={isCustomer ? handleDelete : undefined}
        onAfterVersionUpload={fetchData}
      />

      {/* Upload Dialog */}
      <UploadDialog
        open={uploadDialog.isOpen}
        uploading={uploading}
        categories={categories}
        uploadForm={uploadForm}
        onClose={handleUploadDialogClose}
        onFormChange={setUploadForm}
        onFileChange={handleFileChange}
        onSubmit={handleUploadSubmit}
      />

      {/* Edit Dialog — customer only.
          CR-5325: Edit form mirrors the upload form (Title, Description,
          Folder, Tags, Version, full Visibility picker). Document Type
          and File replacement are intentionally NOT editable here —
          changing the underlying file or doc type post-upload would
          orphan signed download links and read receipts; treat that as
          re-uploading a new doc rather than editing. */}
      <Dialog open={isCustomer && editDialog.isOpen} onOpenChange={(open) => !open && handleEditDialogClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-category">Folder</Label>
              <Select
                value={editForm.category || 'none'}
                onValueChange={(value) => setEditForm({ ...editForm, category: value === 'none' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-tags">Tags</Label>
                <Input
                  id="edit-tags"
                  value={editForm.tags}
                  onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                  placeholder="e.g., contract, invoice"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-version">Version</Label>
                <Input
                  id="edit-version"
                  value={editForm.version}
                  onChange={(e) => setEditForm({ ...editForm, version: e.target.value })}
                  placeholder="e.g., 1.0, v2.1"
                />
              </div>
            </div>
            <VisibilityField
              visibility={editForm.visibility}
              onVisibilityChange={(v) => setEditForm({ ...editForm, visibility: v })}
              sharedCompanyIds={editForm.shared_with_company_ids}
              onSharedCompanyIdsChange={(ids) => setEditForm({ ...editForm, shared_with_company_ids: ids })}
            />

            {/* CR-5329 */}
            <label className="flex items-start gap-3 cursor-pointer rounded-md border p-3 hover:bg-muted/30">
              <input
                type="checkbox"
                checked={editForm.requires_acceptance}
                onChange={(e) => setEditForm({ ...editForm, requires_acceptance: e.target.checked })}
                className="mt-0.5 cursor-pointer"
              />
              <div>
                <p className="text-sm font-medium">Require Read & Accept Receipt</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Recipients must explicitly confirm acceptance. Existing acceptances are preserved.
                </p>
              </div>
            </label>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleEditDialogClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={editSaving}>
                {editSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
